import React, { useState, useMemo, useEffect, useCallback } from "react";
import { usePlans } from "@features/cx/reconnect/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { Badge } from "@shared/ui/badge";
import { useSearchMeter } from "@features/cx/consumer-onboarding/hooks";
import { useReconnectActivateConsumer } from "@features/cx/reconnect/hooks";
import { useDisconnectionRequestDetail } from "@features/cx/disconnect/hooks";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormService } from "@shared/services/FormServices";
import { UseFormReturn } from "react-hook-form";
import { convertDateFormat } from "@shared/utils/convertDateFormat";

interface PlanActivateTabProps {
  premise: string;
  id: string;
}

const PlanActivateTab: React.FC<PlanActivateTabProps> = ({ premise, id }) => {
  const remoteUtilityId = useRemoteUtilityId();
  const [search, setSearch] = useState("");
  const [selectedPlan, setSelectedPlan] = useState<any>(null);

  const {
    data: disconnectRequestData,
    error,
    refetch: refetchDisconnectRequest,
  } = useDisconnectionRequestDetail(Number(id), remoteUtilityId);

  const { data: plans } = usePlans(remoteUtilityId);
  const reconnectMutation = useReconnectActivateConsumer(id);

  const isCompleted = disconnectRequestData?.statusDisplay === "COMPLETED";

  useEffect(() => {
    if (
      isCompleted &&
      disconnectRequestData?.consumer?.remotePlanId &&
      plans &&
      !selectedPlan
    ) {
      const consumerPlan = plans.find(
        (plan: any) => plan.id === disconnectRequestData.consumer.remotePlanId
      );
      if (consumerPlan) {
        setSelectedPlan(consumerPlan);
      }
    }
  }, [isCompleted, disconnectRequestData, plans, selectedPlan]);

  const utilityServices = useMemo(() => {
    const rateData = selectedPlan?.utilityServiceData?.rate;

    if (!rateData || typeof rateData !== "object") return [];

    return Object.entries(rateData)
      .filter(([_, value]: [string, any]) => value?.isMeterRequired)
      .map(([key]) => key.trim());
  }, [selectedPlan]);

  const filteredPlans = useMemo(() => {
    if (!plans) return [];
    if (!search.trim()) return plans;
    return plans.filter(
      (plan: any) =>
        plan.planName.toLowerCase().includes(search.toLowerCase()) ||
        plan.description?.toLowerCase().includes(search.toLowerCase())
    );
  }, [plans, search]);

  const calculateTotalFees = (plan: any) => {
    const tax = plan.tax || 0;
    const processingFees = plan.processingFees || 0;
    const connectionFee = plan.connectionFee || 0;
    const securityDeposit = plan.securityDeposit || 0;
    return processingFees + connectionFee + securityDeposit;
  };

  const handlePlanSelection = (plan: any) => {
    if (!isCompleted) {
      setSelectedPlan(plan);
    }
  };

  const handleChangePlan = () => {
    if (!isCompleted) {
      setSelectedPlan(null);
      setSearch("");
    }
  };

  return (
    <div className="p-6">
      {selectedPlan && (
        <div
          className={`border-2 rounded-2xl p-6 mb-8 relative ${
            isCompleted
              ? "border-green-500 bg-green-50"
              : "border-primary bg-blue-50"
          }`}
        >
          <div
            className={`font-bold text-xl ${
              isCompleted ? "text-green-700" : "text-primary"
            }`}
          >
            {selectedPlan.planName} (Selected)
            {isCompleted && (
              <Badge className="ml-2 bg-green-600 text-white">
                Completed
              </Badge>
            )}
          </div>
          <div className="my-2 text-gray-600">{selectedPlan.description}</div>
          <div
            className={
              isCompleted ? "text-green-700 mb-2" : "text-primary mb-2"
            }
          >
            Billing Frequency: {selectedPlan.billingFrequencyDisplay}
          </div>
          <div className="text-sm text-gray-600">
            Total Fees: ${calculateTotalFees(selectedPlan).toFixed(2)}
          </div>
          {!isCompleted && (
            <Button
              className="absolute top-6 right-6 px-4 py-1.5"
              variant="outline"
              onClick={handleChangePlan}
            >
              Change Plan
            </Button>
          )}
        </div>
      )}

      {!selectedPlan && !isCompleted && (
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4">Plan Selection</h2>
          <Input
            type="text"
            placeholder="Search for plans..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full mb-6 p-2"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPlans.map((plan: any) => (
              <Card
                key={plan.id}
                className="relative flex flex-col cursor-pointer transition-all hover:shadow-md"
                onClick={() => handlePlanSelection(plan)}
              >
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start mb-2">
                    <CardTitle className="text-lg">{plan.planName}</CardTitle>
                    <Badge variant="outline" className="text-xs capitalize">
                      {plan.category}
                    </Badge>
                  </div>
                  <CardDescription className="text-sm">
                    {plan.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 flex-grow">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">
                      ${calculateTotalFees(plan).toFixed(2)}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Total Fees
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium mb-2">
                      Utility Services:
                    </h4>
                    <ul className="space-y-1">
                      {(() => {
                        if (!plan.utilityService) {
                          return (
                            <li className="text-sm text-muted-foreground">
                              No services specified
                            </li>
                          );
                        }
                        let services: string[] = [];
                        if (typeof plan.utilityService === "string") {
                          services = plan.utilityService
                            .split(",")
                            .map((s) => s.trim());
                        } else if (Array.isArray(plan.utilityService)) {
                          services = (plan.utilityService as any[]).map((s) =>
                            String(s).trim()
                          );
                        } else {
                          services = [String(plan.utilityService)];
                        }
                        if (services.length === 0) {
                          return (
                            <li className="text-sm text-muted-foreground">
                              No services specified
                            </li>
                          );
                        }
                        return (
                          <>
                            <li className="text-xs text-muted-foreground flex justify-between font-medium px-1">
                              <span>Service Name</span>
                              <span>Rate Name</span>
                            </li>
                            {services.map((service: string, idx: number) => {
                              const serviceRate =
                                plan.utilityServiceData?.rate?.[service]?.rate
                                  ?.rateName;
                              return (
                                <li
                                  key={idx}
                                  className="text-sm flex justify-between px-1"
                                >
                                  <span>{service}</span>
                                  <span className="text-muted-foreground">
                                    {serviceRate || "-"}
                                  </span>
                                </li>
                              );
                            })}
                          </>
                        );
                      })()}
                    </ul>
                  </div>
                </CardContent>
                <CardFooter className="pt-4">
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePlanSelection(plan);
                    }}
                  >
                    Select Plan
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      )}

      {selectedPlan && utilityServices.length > 0 && (
        <ServiceForm
          utilityServices={utilityServices}
          selectedPlan={selectedPlan}
          isCompleted={isCompleted}
          remoteUtilityId={remoteUtilityId}
          premise={premise}
          disconnectRequestData={disconnectRequestData}
          reconnectMutation={reconnectMutation}
          requestId={id}
          onChangePlan={handleChangePlan}
          refetchDisconnectRequest={refetchDisconnectRequest}
        />
      )}

      {selectedPlan && utilityServices.length === 0 && (
        <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
          <p>No utility services configured for this plan.</p>
        </div>
      )}
    </div>
  );
};

const ServiceForm: React.FC<{
  utilityServices: string[];
  selectedPlan: any;
  isCompleted: boolean;
  remoteUtilityId: string;
  premise: string;
  disconnectRequestData: any;
  reconnectMutation: any;
  requestId: string;
  onChangePlan: () => void;
  refetchDisconnectRequest: () => void;
}> = ({
  utilityServices,
  selectedPlan,
  isCompleted,
  remoteUtilityId,
  premise,
  disconnectRequestData,
  reconnectMutation,
  requestId,
  onChangePlan,
  refetchDisconnectRequest,
}) => {
  const meterQueriesResults = utilityServices.map((service) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const result = useSearchMeter({
      remote_utility_id: remoteUtilityId,
      premise: premise,
      utility_service: service,
    });
    return { service, ...result };
  });

  const meterOptionsByService = useMemo(() => {
    const options: Record<string, any[]> = {};

    meterQueriesResults.forEach(({ service, data, isLoading, error }) => {
      if (!data) {
        options[service] = [];
        return;
      }

      const result =
        data.result || data.data || (Array.isArray(data) ? data : []);

      options[service] = result.map((meter: any) => ({
        label: meter.meterNumber,
        value: meter.id?.toString(),
      }));
    });

    return options;
  }, [meterQueriesResults]);

  
  const initialFormData = useMemo(() => {
    if (isCompleted && disconnectRequestData?.consumer?.consumerMappingData) {
      const services = disconnectRequestData.consumer.consumerMappingData.map(
        (mapping: any) => ({
          serviceName: mapping.utilityService,
          meterId: mapping.meterDetails?.id?.toString() || "",
          meterNumber: mapping.meterDetails?.meterNumber || "",
          reading: mapping.meterDetails?.currentReading?.toString() || "",
         date: convertDateFormat(mapping.meterDetails?.currentReadingDate || ""),
        })
      );
      return { utilityServiceData: services };
    } else if (!isCompleted && utilityServices.length > 0) {
      const services = utilityServices.map((service) => ({
        serviceName: service,
        meterId: "",
        meterNumber: "",
        reading: "",
        date: "",
      }));
      return { utilityServiceData: services };
    }
    return { utilityServiceData: [] };
  }, [isCompleted, disconnectRequestData, utilityServices]);

  const formFields = useMemo(() => {
    return [
      {
        name: "utilityServiceData",
        label: "Utility Services & Meter Readings",
        type: "form-array" as const,
        required: true,
        group: "Service Configuration",
        groupOrder: 1,
        groupColorNeeded: false,
        helperText: isCompleted
          ? "Completed meter readings (Read-only)"
          : "Configure meter readings for each utility service",
        minItems: 1,
        emptyMessage: "No services configured yet.",
        showAddButton: false,
        showRemoveButton: false,
        itemFields: (index: number, formValues?: any) => {
          const serviceName = formValues?.utilityServiceData?.[index]?.serviceName || "";

          let options = meterOptionsByService[serviceName] || [];

          if (isCompleted && initialFormData.utilityServiceData[index]) {
            const currentMeterId = initialFormData.utilityServiceData[index].meterId;
            const currentMeterNumber = initialFormData.utilityServiceData[index].meterNumber;

            if (currentMeterId && currentMeterNumber) {
              const meterExists = options.some(
                (option: any) => option.value === currentMeterId
              );
              if (!meterExists) {
                options = [{
                  label: currentMeterNumber,
                  value: currentMeterId,
                }, ...options];
              }
            }
          }

          return [
            {
              name: "serviceName",
              label: "Service Name",
              type: "text" as const,
              required: true,
              disabled: true,
              placeholder: "Service Name",
            },
            {
              name: "meterId",
              label: "Select Meter",
              type: "select" as const,
              required: true,
              disabled: isCompleted,
              placeholder: "Select Meter",
              options: options,
            },
            {
              name: "reading",
              label: "Latest Reading",
              type: "number" as const,
              required: true,
              disabled: isCompleted,
              min: 0,
              placeholder: "Enter reading",
            },
            {
              name: "date",
              label: "Latest Reading Date",
              type: "datepicker" as const,
              required: true,
              disabled: isCompleted,
              allowFuture: false,
              placeholder: "Select date",
            },
          ];
        },
      },
    ];
  }, [isCompleted, meterOptionsByService, initialFormData]);

  const form = FormService.createForm({
    fields: formFields,
    defaultValues: initialFormData,
    mode: "onBlur" as const,
  });

  useEffect(() => {
    if (initialFormData.utilityServiceData.length > 0) {
      form.setValue("utilityServiceData", initialFormData.utilityServiceData);
    }
  }, [initialFormData, form]);

  const handleSubmit = useCallback(
    (data: any) => {
      if (isCompleted) return;

      const utility_service_meter_data: Record<string, any> = {};

      data.utilityServiceData.forEach((service: any) => {
        utility_service_meter_data[service.serviceName] = {
          meter_id: service.meterId || "",
          current_reading: service.reading ? Number(service.reading) : null,
          current_reading_date: service.date || "",
        };
      });

      const payload = {
        remote_plan_id: selectedPlan.id,
        utility_service_meter_data,
      };

      reconnectMutation.mutate(
        { requestId, payload },
        {
          onSuccess: () => {
            refetchDisconnectRequest();
          },
          onError: (error: any) => {
          },
        }
      );
    },
    [
      isCompleted,
      selectedPlan,
      reconnectMutation,
      requestId,
      refetchDisconnectRequest,
    ]
  );

  const renderCustomFooter = useCallback(() => {
    if (isCompleted) return null;

    return (
      <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
        <Button
          variant="outline"
          type="button"
          onClick={onChangePlan}
          disabled={reconnectMutation.isPending}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={reconnectMutation.isPending}>
          {reconnectMutation.isPending ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              Reconnecting...
            </>
          ) : (
            "Reconnect Service"
          )}
        </Button>
      </div>
    );
  }, [isCompleted, onChangePlan, reconnectMutation.isPending]);

  return (
    <DynamicForm
      fields={formFields}
      form={form}
      onSubmit={handleSubmit}
      loading={reconnectMutation.isPending}
      renderCustomFooter={renderCustomFooter}
      gridColumns={1}
      config={{
        layout: "vertical",
        spacing: "comfortable",
        showResetButton: false,
        showSubmitButton: false,
      }}
    />
  );
};

export default PlanActivateTab;